import React, { useMemo } from 'react';

import { Card, Row, Col, Progress, Table, Tag, Empty } from 'antd';
import { useTranslation } from 'react-i18next';

// 統一色盤
const COLORS = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2'];

/**
 * ChannelCharts - 通路圖表組件
 *
 * @param {Object} props
 * @param {Object} props.data - 分析數據
 * @param {Object} props.channelMap - 通路映射
 */
const ChannelCharts = ({ data, channelMap = {} }) => {
  const { t } = useTranslation();
  const cs = data?.metadata?.currency_symbol || '$';
  const dp = data?.metadata?.currency_decimal_places ?? 2;

  const channelStats = useMemo(() => {
    if (!data || !data.current || !data.current.by_channel) {
      return [];
    }

    const byChannel = data.current.by_channel;
    const totalRevenue = Object.values(byChannel).reduce((sum, c) => sum + (c.revenue || 0), 0);

    // 建立通路明細的退貨率/取消率快查表
    const detailsMap = {};
    (data.channel_details || []).forEach((cd) => {
      detailsMap[cd.id] = cd;
    });

    return Object.entries(byChannel)
      .map(([channelId, stats]) => {
        const channel = channelMap[channelId];
        const detail = detailsMap[channelId] || {};
        return {
          channelId,
          name: channel?.name || `Channel ${channelId}`,
          orders: stats.count || 0,
          quantity: stats.quantity || 0,
          revenue: stats.revenue || 0,
          avg_order_value: (stats.count || 0) > 0 ? (stats.revenue || 0) / stats.count : 0,
          percentage: totalRevenue > 0 ? (stats.revenue / totalRevenue) * 100 : 0,
          return_rate: detail.return_rate ?? 0,
          cancellation_rate: detail.cancellation_rate ?? 0,
        };
      })
      .sort((a, b) => b.revenue - a.revenue);
  }, [data, channelMap]);

  if (!channelStats || channelStats.length === 0) {
    return (
      <Card title={t('shared.analytics.channel_charts.title')}>
        <Empty description={t('common.empty.no_data')} />
      </Card>
    );
  }

  const totalOrders = channelStats.reduce((sum, s) => sum + s.orders, 0);

  // 為每個通路分配顏色
  const coloredStats = channelStats.map((stat, index) => ({
    ...stat,
    color: COLORS[index % COLORS.length],
  }));

  // 表格欄位
  const columns = [
    {
      title: t('shared.analytics.channel_charts.channel'),
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <span>
          <Tag color={record.color}>{name}</Tag>
        </span>
      ),
    },
    {
      title: t('shared.analytics.channel_charts.orders'),
      dataIndex: 'orders',
      key: 'orders',
      sorter: (a, b) => a.orders - b.orders,
      render: (value) => value.toLocaleString(),
    },
    {
      title: t('shared.analytics.channel_charts.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      sorter: (a, b) => a.quantity - b.quantity,
      render: (value) => value.toLocaleString(),
    },
    {
      title: t('shared.analytics.channel_charts.revenue'),
      dataIndex: 'revenue',
      key: 'revenue',
      sorter: (a, b) => a.revenue - b.revenue,
      render: (value) => `${cs}${value.toFixed(dp)}`,
    },
    {
      title: t('shared.analytics.channel_charts.avg_order_value'),
      dataIndex: 'avg_order_value',
      key: 'avg_order_value',
      sorter: (a, b) => a.avg_order_value - b.avg_order_value,
      render: (value) => `${cs}${(value || 0).toFixed(dp)}`,
    },
    {
      title: t('shared.analytics.channel_charts.percentage'),
      dataIndex: 'percentage',
      key: 'percentage',
      sorter: (a, b) => a.percentage - b.percentage,
      render: (value) => `${value.toFixed(1)}%`,
    },
    {
      title: t('shared.analytics.channel_charts.return_rate'),
      dataIndex: 'return_rate',
      key: 'return_rate',
      sorter: (a, b) => a.return_rate - b.return_rate,
      render: (value) => (
        <span style={{ color: value > 5 ? '#ff4d4f' : undefined }}>
          {(value || 0).toFixed(1)}%
        </span>
      ),
    },
    {
      title: t('shared.analytics.channel_charts.cancellation_rate'),
      dataIndex: 'cancellation_rate',
      key: 'cancellation_rate',
      sorter: (a, b) => a.cancellation_rate - b.cancellation_rate,
      render: (value) => (
        <span style={{ color: value > 10 ? '#ff4d4f' : undefined }}>
          {(value || 0).toFixed(1)}%
        </span>
      ),
    },
  ];

  const renderProgressList = (items, getValue, getLabel) => (
    items.map((item) => {
      const { value, percent } = getValue(item);
      return (
        <div key={item.channelId} style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <Tag color={item.color}>{item.name}</Tag>
            <span style={{ fontWeight: 500, fontSize: 13 }}>{getLabel(value, percent)}</span>
          </div>
          <Progress percent={percent} strokeColor={item.color} showInfo={false} size="small" />
        </div>
      );
    })
  );

  return (
    <Row gutter={[16, 16]}>
      {/* 通路營業額佔比 */}
      <Col xs={24} lg={12}>
        <Card title={t('shared.analytics.channel_charts.revenue_distribution')} size="small">
          {renderProgressList(
            coloredStats,
            (item) => ({ value: item.revenue, percent: item.percentage }),
            (value, percent) => `${cs}${value.toFixed(dp)} (${percent.toFixed(1)}%)`
          )}
        </Card>
      </Col>

      {/* 通路訂單數佔比 */}
      <Col xs={24} lg={12}>
        <Card title={t('shared.analytics.channel_charts.orders_distribution')} size="small">
          {renderProgressList(
            coloredStats,
            (item) => ({
              value: item.orders,
              percent: totalOrders > 0 ? (item.orders / totalOrders) * 100 : 0,
            }),
            (value, percent) => `${value.toLocaleString()} (${percent.toFixed(1)}%)`
          )}
        </Card>
      </Col>

      {/* 通路統計表格 */}
      <Col span={24}>
        <Card title={t('shared.analytics.channel_charts.statistics_table')} size="small">
          <Table
            dataSource={coloredStats}
            columns={columns}
            rowKey="channelId"
            pagination={false}
            size="small"
          />
        </Card>
      </Col>
    </Row>
  );
};

export default ChannelCharts;
